import {
  Agreement02Icon,
  Target02Icon,
  CustomerSupportIcon,
  ChartLineData02Icon,
  ChartLineData01Icon,
  ChartIncreaseIcon,
  StarIcon,
} from '@hugeicons/core-free-icons';

/**
 * Optional per-playbook agent description overrides.
 * Keys are agent values (e.g. 'rag', 'pollingAgent'); values are playbook-specific descriptions.
 * Omit or use empty object to fall back to totalAgentTypes descriptions.
 * @typedef {{ [agentValue: string]: string }} AgentDescriptions
 */

export const PlaybookType = {
  ACTIVATION: {
    value: 'activation',
    title: 'Activation',
    icon: Target02Icon,
    shortDescription:
      'Identify, prioritise, and activate the audiences and sponsors most likely to engage.',
    longDescription:
      'Helps event teams move beyond broad outreach by identifying high-propensity audiences, re-activating dormant contacts, and capturing early intent signals. Engagement becomes targeted, measurable, and aligned to real interest before conversion begins.',
    agentTypes: ['rag', 'pollingAgent', 'qualification', 'ptEmailChannel'],
    agentDescriptions: {
      rag: 'Your attendees go-to concierge. It helps them discover relevant sessions, content, and info in real time.',
      pollingAgent:
        'Figures out what attendees are into, then keeps tuning the experience to match.',
      qualification:
        "Flags high-value prospects the moment they're ready to convert, so follow-up hits at the right time.",
      ptEmailChannel:
        'Runs personalised recommendations for a contact list — from HubSpot or a spreadsheet — without needing a website visit.',
    },
  },

  NURTURE_CONVERSION: {
    value: 'nurture_conversion',
    title: 'Nurture & Conversion',
    icon: ChartIncreaseIcon,
    shortDescription: 'Guide interested visitors towards registrations, bookings, or submissions.',
    longDescription:
      'Supports prospects during the evaluation phase by responding to questions, surfacing relevant sessions or sponsors, and guiding each user towards their most relevant next step. Converts passive interest into measurable actions through contextual, conversational progression.',
    agentTypes: ['rag', 'pollingAgent', 'recirculation', 'qualification'],
    agentDescriptions: {
      rag: "Your attendees' go-to concierge. It helps them discover relevant sessions, content, and info in real time.",
      pollingAgent:
        'Figures out what attendees are into, then keeps tuning the experience to match.',
      recirculation:
        'Gives readers AI-powered summaries, insights, and recommendations that fit them personally.',
      qualification:
        "Flags high-value prospects the moment they're ready to convert, so follow-up hits at the right time.",
    },
  },

  HYPER_PERSONALISED_JOURNEY: {
    value: 'hyper_personalised_event_journey',
    title: 'Hyper-Personalised Event Journey',
    icon: StarIcon,
    shortDescription:
      'Deliver tailored event guidance, recommendations, and discovery experiences.',
    longDescription:
      'Creates a continuously personalised event experience by adapting recommendations, answers, and discovery paths based on attendee behaviour and priorities. Helps participants find the most relevant sessions, sponsors, and opportunities with minimal friction.',
    agentTypes: ['rag', 'pollingAgent', 'recirculation'],
    agentDescriptions: {
      rag: "Your attendees' go-to concierge. It helps them discover relevant sessions, content, and info in real time.",
      pollingAgent:
        'Figures out what attendees are into, then keeps tuning the experience to match.',
      recirculation:
        'Gives readers AI-powered summaries, insights, and recommendations that fit them personally.',
    },
  },

  SPONSOR_MAXIMISATION: {
    value: 'sponsor_maximisation',
    title: 'Sponsor Maximisation',
    icon: ChartLineData02Icon,
    shortDescription:
      'Connect buyer intent with the right sponsors and produce measurable ROI signals.',
    longDescription:
      'Transforms sponsorship from passive exposure into measurable engagement by routing qualified attendees to relevant sponsors, capturing interaction signals, and supporting sponsor conversations at the right moment. Strengthens reporting, renewal confidence, and commercial value.',
    agentTypes: ['rag', 'pollingAgent', 'qualification'],
    agentDescriptions: {
      rag: "Your attendees' go-to concierge. It helps them discover relevant sessions, content, and info in real time.",
      pollingAgent:
        'Figures out what attendees are into, then keeps tuning the experience to match.',
      qualification:
        "Flags high-value prospects the moment they're ready to convert, so follow-up hits at the right time.",
    },
  },

  // Connections (portal 4.9.5): a workflow of its own, where an event's Connection agents are made and run.
  CONNECTIONS: {
    value: 'connections',
    title: 'Connections',
    icon: Agreement02Icon,
    shortDescription:
      'Match delegates with sponsors and with each other, and turn the matches into meetings.',
    longDescription:
      "Bridged's matching engine suggests who should meet at the event. The events team reviews the matches and publishes them to the Connection app, where delegates and sponsors choose who to meet, pick a time and say how it went. The team steps in on meetings, reaches people, and reads the event report.",
    agentTypes: ['connection'],
    agentDescriptions: {
      connection:
        'Suggests who should meet, then lets both sides choose and book in the Connection app, with feedback after each meeting and an event report.',
    },
  },

  OPS_DEFLECTION: {
    value: 'ops_deflection',
    title: 'Ops Deflection',
    icon: CustomerSupportIcon,
    shortDescription: 'Automatically resolve repeat queries and reduce operational support load.',
    longDescription:
      'Handles high-volume attendee, sponsor, and exhibitor queries by providing instant, accurate answers from event knowledge sources. Reduces inbox pressure, prevents repetitive support work, and allows teams to focus only on complex or high-priority issues.',
    agentTypes: ['rag'],
    agentDescriptions: {
      rag: "Your attendees' go-to concierge. It helps them discover relevant sessions, content, and info in real time.",
    },
  },

  DATA_DECISION_INTELLIGENCE: {
    value: 'data_decision_intelligence',
    title: 'Data-Driven Decision Intelligence',
    icon: ChartLineData01Icon,
    shortDescription: 'Turn event and audience data into actionable portfolio insights.',
    longDescription:
      'Aggregates interaction, engagement, and performance data across events to reveal patterns, benchmark outcomes, and support better commercial and operational decisions. Helps teams understand what drives revenue, engagement, and sponsor success over time.',
    agentTypes: ['DEA'],
    agentDescriptions: {
      DEA: 'Digs into cross-event data, spots performance patterns, and surfaces insights to guide planning, pricing, and strategy.',
    },
  },
  DEFAULT_PLAYBOOK: {
    value: 'default_playbook',
    title: 'Default Workflow',
    icon: StarIcon,
    shortDescription: 'Default workflow for new users.',
    longDescription: 'Default workflow for new users.',
    agentTypes: ['rag', 'pollingAgent', 'recirculation', 'qualification', 'DEA', 'xRay'],
    agentDescriptions: {
      rag: "Your attendees' go-to concierge. It helps them discover relevant sessions, content, and info in real time.",
      pollingAgent:
        'Figures out what attendees are into, then keeps tuning the experience to match.',
      recirculation:
        'Gives readers AI-powered summaries, insights, and recommendations that fit them personally.',
      qualification:
        "Flags high-value prospects the moment they're ready to convert, so follow-up hits at the right time.",
      DEA: 'Digs into cross-event data, spots performance patterns, and surfaces insights to guide planning, pricing, and strategy.',
      ptEmailChannel:
        'Runs personalised recommendations for a contact list — from HubSpot or a spreadsheet — without needing a website visit.',
    },
    isDefault: true,
    id: '',
  },
};
